import {Container, ContainerModule, interfaces} from "inversify";
import {IUserStore, UserStore} from "data/stores/user/user.store";
import {HeaderController, IHeaderController} from "views/components/header/header.controller";

import {AuthController, IAuthController} from "views/controllers/auth/auth.controller";
import {
	CreateLeagueController,
	ICreateLeagueController,
} from "views/pages/create_league/create_league.controller";
import {
	ILeaguesToJoinController,
	LeaguesToJoinController,
} from "views/pages/leagues_to_join/leagues_to_join.controller";
import {JSON_URL, API_URL, CONTENT_JSON_URL} from "data/constants";
import {type IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, type IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, type IHttpClientService} from "data/services/http";

import {
	GameplayAPIProvider,
	type IGameplayApiProvider,
} from "data/providers/api/gameplay.api.provider";
import {GameplayStore, type IGameplayStore} from "data/stores/gameplay/gameplay.store";

import {type IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {type IModalsController, ModalsController} from "views/controllers/modals/modals.controller";
import {HomeController, type IHomeController} from "views/pages/home/home.controller";
import {
	type ILocalizationStore,
	LocalizationStore,
} from "data/stores/localization/localization.store";
import {
	type ILocalizationApiProvider,
	LocalizationApiProvider,
} from "data/providers/api/localization.api.provider";
import {
	GameGateController,
	type IGameGateController,
} from "views/components/game_gate/game_gate.controller";
import {
	type ILocalizationController,
	LocalizationController,
} from "views/controllers/localization/localization.controller";

import {
	type ILeaguesApiProvider,
	LeaguesApiProvider,
} from "data/providers/api/leagues.api.provider";
import {
	type IMyLeaguesController,
	MyLeaguesController,
} from "views/pages/my_leagues/my_leagues.controller";
import {
	ContestController,
	type IContestController,
} from "views/controllers/contest/contest.controller";

import {type ILeagueController, LeagueController} from "views/pages/league/league.controller";
import {
	type IInviteLeagueController,
	InviteLeagueController,
} from "views/components/leagues/league_invite_form/league.invite_form.controller";
import {
	type ISettingsLeagueController,
	SettingsLeagueController,
} from "views/pages/league_settings/league_settings.controller";
import {
	ContestBannerController,
	type IContestBannerController,
} from "views/components/contest/contest_banner/contest_banner.contoller";
import {
	type ILeagueUsersController,
	LeagueUsersController,
} from "views/components/leagues/league_users/league_users.controller";
import {
	type IMyAccountDetailsController,
	MyAccountDetailsController,
} from "views/components/my_account/my_account_details/my_account_details.controller";

import {
	type ISettingsLeagueFormController,
	SettingsLeagueFormController,
} from "views/components/leagues/league_settings_form/league_settings_form.controller";
import {type ILandingController, LandingController} from "views/pages/landing/landing.controller";
import {
	ContestQuestionCardController,
	type IContestQuestionCardController,
} from "views/components/contest/contest_question_card/contest_question_card.controller";

import {
	type IStandingsController,
	StandingsController,
} from "views/controllers/standings/standings.controller";

import {
	type IStandingsApiProvider,
	StandingsApiProvider,
} from "data/providers/api/standings.api.provider";

import {IStandingsStore, StandingsStore} from "data/stores/standings/standings.store";

import {HelpController, IHelpController} from "views/pages/help/help.controller";
import {
	HelpListController,
	type IHelpListController,
} from "views/components/help/help_list/help_list.controller";
import {
	ContactUsController,
	type IContactUsController,
} from "views/components/help/contact_us/contact_us.controller";
import {FormValidationHelper, type IFormValidationHelper} from "data/utils/form_validation_helper";
import {
	type ILeagueControlsController,
	LeagueControlsController,
} from "views/components/leagues/league_controls/league_controls.controller";
import {Bindings} from "data/constants/bindings";
import {
	type IStaticContentStore,
	StaticContentStore,
} from "data/stores/static_content/static_content.store";
import {type ILeaguesStore, LeaguesStore} from "data/stores/leagues/leagues.store";
import {type IPrizesController, PrizesController} from "views/pages/prizes/prizes.controller";
import {
	ISecondRegistrationFormController,
	SecondRegistrationFormController,
} from "views/components/forms/second_registration_form/second_registration_form.controller";
import {
	IContestRootController,
	ContestRootController,
} from "views/components/contest/contest_root/contest_root.controller";
import {
	ICFLHeaderController,
	CFLHeaderController,
} from "views/components/cfl_header/cfl_header.controller";
import {
	CFLHeaderDesktopController,
	ICFLHeaderDesktopController,
} from "views/components/cfl_header/cfl_header_desktop/cfl_header_desktop.controller";
import {
	ICFLHeaderMobileController,
	CFLHeaderMobileController,
} from "views/components/cfl_header/cfl_header_mobile/cfl_header_mobile.controller";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);

	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.ContentJsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: CONTENT_JSON_URL,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<IGameplayApiProvider>(Bindings.GameplayApiProvider).to(GameplayAPIProvider);
	bind<ILocalizationApiProvider>(Bindings.LocalizationApiProvider).to(LocalizationApiProvider);
	bind<ILeaguesApiProvider>(Bindings.LeaguesApiProvider).to(LeaguesApiProvider);
	bind<IStandingsApiProvider>(Bindings.StandingsApiProvider).to(StandingsApiProvider);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<IGameplayStore>(Bindings.GameplayStore).to(GameplayStore).inSingletonScope();
	bind<ILocalizationStore>(Bindings.LocalizationStore).to(LocalizationStore).inSingletonScope();
	bind<ILeaguesStore>(Bindings.LeaguesStore).to(LeaguesStore).inSingletonScope();
	bind<IStaticContentStore>(Bindings.StaticContentStore)
		.to(StaticContentStore)
		.inSingletonScope();
	bind<IStandingsStore>(Bindings.StandingsStore).to(StandingsStore);
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IAuthController>(Bindings.AuthController).to(AuthController);

	bind<IModalsController>(Bindings.ModalsController).to(ModalsController);
	bind<IHomeController>(Bindings.HomeController).to(HomeController);
	bind<IHeaderController>(Bindings.HeaderController).to(HeaderController);
	bind<IGameGateController>(Bindings.GameGateController).to(GameGateController);
	bind<ILocalizationController>(Bindings.LocalizationController).to(LocalizationController);
	bind<ICreateLeagueController>(Bindings.CreateLeagueController).to(CreateLeagueController);
	bind<IMyLeaguesController>(Bindings.MyLeaguesController).to(MyLeaguesController);
	bind<ILeaguesToJoinController>(Bindings.LeaguesToJoinController).to(LeaguesToJoinController);
	bind<IContestController>(Bindings.ContestController).to(ContestController);
	bind<ILeagueController>(Bindings.LeagueController).to(LeagueController);
	bind<IInviteLeagueController>(Bindings.InviteLeagueController).to(InviteLeagueController);
	bind<ILeagueUsersController>(Bindings.LeagueUsersController).to(LeagueUsersController);
	bind<ILeagueControlsController>(Bindings.LeagueControlsController).to(LeagueControlsController);
	bind<ISettingsLeagueController>(Bindings.SettingsLeagueController).to(SettingsLeagueController);
	bind<ISettingsLeagueFormController>(Bindings.SettingsLeagueFormController).to(
		SettingsLeagueFormController
	);
	bind<IContestBannerController>(Bindings.ContestBannerController).to(ContestBannerController);
	bind<IMyAccountDetailsController>(Bindings.MyAccountDetailsController).to(
		MyAccountDetailsController
	);

	bind<ILandingController>(Bindings.LandingController).to(LandingController);
	bind<IContestQuestionCardController>(Bindings.ContestQuestionCardController).to(
		ContestQuestionCardController
	);

	bind<IHelpController>(Bindings.HelpController).to(HelpController);

	bind<IStandingsController>(Bindings.StandingsController).to(StandingsController);

	bind<IHelpListController>(Bindings.HelpListController).to(HelpListController);

	bind<IContactUsController>(Bindings.ContactUsController).to(ContactUsController);
	bind<IPrizesController>(Bindings.PrizesController).to(PrizesController);
	bind<ISecondRegistrationFormController>(Bindings.SecondRegistrationFormController).to(
		SecondRegistrationFormController
	);
	bind<IContestRootController>(Bindings.ContestRootController).to(ContestRootController);

	/**
	 * CFL Header
	 */
	bind<ICFLHeaderController>(Bindings.CFLHeaderController).to(CFLHeaderController);
	bind<ICFLHeaderDesktopController>(Bindings.CFLHeaderDesktopController).to(
		CFLHeaderDesktopController
	);
	bind<ICFLHeaderMobileController>(Bindings.CFLHeaderMobileController).to(
		CFLHeaderMobileController
	);
});

export const helpers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IFormValidationHelper>(Bindings.FormValidationHelper).to(FormValidationHelper);
});
